import { X } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { setImagePredictionMenu } from '../Redux/store';
import ImageDisplay from '../components/ImageDisplay';

export default function ImagePredictionMenu() {
  const dispatch = useDispatch();
  const imagePredictionMenu = useSelector((s) => s.ui.imagePredictionMenu);

  if (!imagePredictionMenu) return null;

  const { test, predictionData } = imagePredictionMenu;
  const { boxes, value } = predictionData;

  const handleClose = () => {
    dispatch(setImagePredictionMenu(null));
  };

  return (
    <div className="fixed inset-0 z-[1001] flex items-center justify-center" style={{ paddingBottom: '80px' }}>
      <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-[90%] max-w-md flex flex-col rounded-3xl bg-card border border-primary shadow-xl overflow-hidden" style={{ maxHeight: 'calc(90vh - 72px)' }}>
        <button onClick={handleClose} className="absolute right-4 top-4 z-10 text-muted-foreground">
          <X className="h-5 w-5" />
        </button>
        <div className="bg-gradient-to-br from-primary to-accent p-6 text-primary-foreground shrink-0">
          <h2 className="text-2xl font-bold">AI Prediction Details</h2>
          <p className="text-sm opacity-90 mt-1">Value: {value?.toFixed(3) || 'N/A'}</p>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {/* Image with rectangle overlay */}
          <div className="relative w-full aspect-[6/1] bg-secondary rounded-lg overflow-hidden mb-4">
            <ImageDisplay
              imageData={test.photo_url || test.fileUrl || test.imageUrl}
              showIcons={false}
              showLoading={true}
              imageStrategy="contain"
              className="w-full h-full"
            />
            {/* Rectangle overlay */}
            {boxes && boxes.length > 0 && boxes[0].bbox && (
              <BoundingBoxOverlay bbox={boxes[0].bbox} />
            )}
          </div>

          {/* Prediction details */}
          {boxes && boxes.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Detection Details</h3>
              {boxes.map((box, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-muted border border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-foreground">{box.label}</span>
                  </div>
                  {box.bbox && (
                    <div className="text-xs text-muted-foreground mt-1">
                      BBox: [{box.bbox.map((v, i) => v.toFixed(3)).join(', ')}]
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 pt-0 shrink-0">
          <button
            onClick={handleClose}
            className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground shadow"
            style={{ marginTop: '5px' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** Component to draw rectangle overlay based on bbox coordinates */
function BoundingBoxOverlay({ bbox }) {
  if (!bbox) return null;
  
  // bbox is [x1, y1, x2, y2] in pixel coordinates (from 384x64 image)
  // Convert to normalized coordinates (0-1) then to percentages
  const [x1, y1, x2, y2] = bbox;
  const imageWidth = 384;
  const imageHeight = 64;
  
  const rectStyle = {
    left: `${(x1 / imageWidth) * 100}%`,
    top: `${(y1 / imageHeight) * 100}%`,
    width: `${((x2 - x1) / imageWidth) * 100}%`,
    height: `${((y2 - y1) / imageHeight) * 100}%`,
  };

  return (
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute border-2 border-green-400 bg-green-400/20"
        style={rectStyle}
      />
    </div>
  );
}
